import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../Context/LanguageContext";
import { FiCode, FiBriefcase, FiFile, FiAward, FiStar } from "react-icons/fi";
import {
  SiJavascript,
  SiReact,
  SiNodedotjs,
  SiAngular,
  SiNextdotjs,
  SiGraphql,
  SiTypescript,
  SiHtml5,
  SiCss3,
  SiVuedotjs,
  SiNestjs,
  SiExpress,
  SiWebcomponentsdotorg,
} from "react-icons/si";
import s1 from "../Assets/s1.png";
import s2 from "../Assets/s2.png";
import s3 from "../Assets/s3.png";
import s4 from "../Assets/s4.png";
import s5 from "../Assets/s5.png";
import s6 from "../Assets/s6.png";
import "./about.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const skills = [
  { name: "JavaScript", icon: <SiJavascript />, color: "#F7DF1E", level: 90 },
  {
    name: "React",
    icon: <SiReact />,
    color: "#61DAFB",
    level: 92,
  },
  { name: "Node.js", icon: <SiNodedotjs />, color: "#68A063", level: 75 },
  {
    name: "TypeScript",
    icon: <SiTypescript />,
    color: "#3178C6",
    level: 80,
  },
  { name: "Vue", icon: <SiVuedotjs />, color: "#68A063", level: 65 },
  {
    name: "Angular",
    icon: <SiAngular />,
    color: "#DD0031",
    level: 80,
  },
  { name: "Next.js", icon: <SiNextdotjs />, color: "#E5E9F0", level: 72 },
  {
    name: "GraphQL (Apollo Client)",
    icon: <SiGraphql />,
    color: "#E10098",
    level: 70,
  },
  { name: "HTML5", icon: <SiHtml5 />, color: "#E34F26", level: 95 },
  {
    name: "CSS3",
    icon: <SiCss3 />,
    color: "#1572B6",
    level: 90,
  },
  {
    name: "Nest",
    icon: <SiNestjs />,
    color: "#DD0031",
    level: 65,
  },
  {
    name: "Express",
    icon: <SiExpress />,
    color: "#F7DF1E",
    level: 70,
  },
  {
    name: "Web Component",
    icon: <SiWebcomponentsdotorg />,
    color: "#F7DF1E",
    level: 85,
  },
];

const certs = [s1, s2, s3, s4, s5, s6];

const SkillBar = ({ skill, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <motion.div
      ref={ref}
      className="skill-item"
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.06, duration: 0.4 }}
    >
      <div className="skill-item__header">
        <span className="skill-item__icon" style={{ color: skill.color }}>
          {skill.icon}
        </span>
        <span className="skill-item__name mono">{skill.name}</span>
        <span className="skill-item__level mono">{skill.level}%</span>
      </div>
      <div className="skill-item__bar-bg">
        <motion.div
          className="skill-item__bar"
          style={{ background: skill.color }}
          initial={{ width: 0 }}
          animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
          transition={{ delay: index * 0.06 + 0.3, duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
};

const TimelineItem = ({ exp, index, t }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <motion.div
      ref={ref}
      className="timeline-item"
      initial={{ opacity: 0, x: 30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.15, duration: 0.5 }}
    >
      <div className="timeline-item__dot" />
      <div className="timeline-item__content">
        <div className="timeline-item__header">
          <span className="timeline-item__type mono">{t(exp.type)}</span>
          <span className="timeline-item__period mono">
            {exp.period.replace("present", t("Present"))}
          </span>
        </div>
        <h3 className="timeline-item__role">{t(exp.role)}</h3>
        <p className="timeline-item__company" style={{ color: "var(--accent-cyan)" }}>
          @ {exp.company}
        </p>
        <p className="timeline-item__desc">{t(exp.descKey)}</p>
        <div className="timeline-item__tags">
          {exp.tech.map((tag) => (
            <span key={tag} className="timeline-item__tag mono">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const CertCarousel = () => {
  const [current, setCurrent] = useState(0);
  return (
    <div className="cert-carousel">
      <div className="cert-carousel__header mono" style={{ color: "var(--text-muted)" }}>
        <FiAward style={{ marginRight: 8, color: "var(--accent-yellow)" }} />
        {"// Certifications"}
      </div>
      <div className="cert-carousel__track">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={certs[current]}
            alt={`Certificate ${current + 1}`}
            className="cert-carousel__img"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.4 }}
          />
        </AnimatePresence>
      </div>
      <div className="cert-carousel__controls">
        <button
          className="cert-carousel__btn"
          onClick={() => setCurrent((c) => (c - 1 + certs.length) % certs.length)}
        >
          ‹
        </button>
        <div className="cert-carousel__dots">
          {certs.map((_, i) => (
            <button
              key={i}
              className={`cert-carousel__dot${i === current ? " cert-carousel__dot--active" : ""}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
        <button
          className="cert-carousel__btn"
          onClick={() => setCurrent((c) => (c + 1) % certs.length)}
        >
          ›
        </button>
      </div>
    </div>
  );
};

const AboutMe = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState("skills");

  const experience = [
    {
      role: "SoftwareEngineer",
      company: "Weidmüller GTI Software GmbH",
      period: "2026 – present",
      type: "Fulltime",
      descKey: "Exp_Weidmuller",
      tech: ["JavaScript", "TypeScript", "Angular", "C#"],
    },
    {
      role: "FrontendFocus",
      company: "tech11 GmbH",
      period: "2021 – 2026",
      type: "Fulltime",
      descKey: "Exp_Tech11",
      tech: ["Angular", "TypeScript", "Web Components", "Lit-HTML", "JavaScript"],
    },
    {
      role: "FullstackDev",
      company: "Prunedge",
      period: "2020 – 2023",
      type: "Fulltime",
      descKey: "Exp_Prunedge",
      tech: ["React", "Next.js", "TypeScript", "Node.js", "MongoDB", "Tailwind CSS"],
    },
    {
      role: "FrontendDev",
      company: "Facility Bill",
      period: "2019 – 2020",
      type: "Fulltime",
      descKey: "Exp_FacilityBill",
      tech: ["React", "JavaScript", "REST APIs", "CSS"],
    },
    {
      role: "FrontendDev",
      company: "Philometer",
      period: "2023 – 2024",
      type: "Contract",
      descKey: "Exp_Philometer",
      tech: ["React", "React Query", "Tailwind CSS", "shadcn/ui", "JavaScript"],
    },
  ];

  const tabs = [
    { key: "skills", label: "SkillsTab", icon: <FiCode size={14} /> },
    {
      key: "experience",
      label: "ExperienceTab",
      icon: <FiBriefcase size={14} />,
    },
    { key: "certs", label: "CertsTab", icon: <FiAward size={14} /> },
  ];

  const interests = [
    { key: "Interest_WebDev", icon: <FiFile size={12} /> },
    { key: "Interest_OpenSource", icon: <FiFile size={12} /> },
    { key: "Interest_Web3", icon: <FiFile size={12} /> },
    { key: "Interest_Prompting", icon: <FiFile size={12} /> },
    { key: "Interest_AI", icon: <FiFile size={12} /> },
    { key: "Interest_Football", icon: <FiFile size={12} /> },
  ];

  return (
    <motion.div
      className="about-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      {/* Left panel: bio/info */}
      <div className="about-page__info">
        <div className="about-info__tab-header mono">{t("PersonalInfo")}</div>
        <div className="about-info__code">
          {[
            {
              n: 1,
              content: (
                <>
                  <span className="kw">{"/**"}</span>
                </>
              ),
            },
            {
              n: 2,
              content: (
                <>
                  {" "}
                  * <span className="str">{t("AboutMeTitle")}</span>
                </>
              ),
            },
            { n: 3, content: <> * Full-stack Engineer with 5+ years</> },
            { n: 4, content: <> * building scalable web applications</> },
            { n: 5, content: <> * reusable UI systems and robust API.</> },
            { n: 6, content: <> * Experienced in React, Angular, Vue, Node</> },
            { n: 7, content: <> * and modern TypeScript architectures.</> },
            { n: 8, content: <> * Focused on performance,</> },
            { n: 9, content: <> * maintainability and clean code.</> },
            { n: 10, content: <> * Proficient in:</> },
            { n: 11, content: <> * React, Angular, Next.js, TypeScript</> },
            { n: 12, content: <> * Node.js, GraphQL, REST APIs</> },
            {
              n: 13,
              content: (
                <>
                  <span className="kw">{"**/"}</span>
                </>
              ),
            },
          ].map(({ n, content }) => (
            <div key={n} className="about-info__line">
              <span className="ln">{n}</span>
              <span>{content}</span>
            </div>
          ))}
        </div>

        <div className="about-info__interests">
          <p className="about-info__interests-title mono" style={{ color: "var(--text-muted)" }}>
            <FiStar style={{ marginRight: 6, color: "var(--accent-yellow)" }} />
            {t("Interests")}
          </p>
          {interests.map((interest) => (
            <motion.div
              key={interest.key}
              className="interest-tag"
              whileHover={{ scale: 1.04, borderColor: "var(--accent-cyan)" }}
            >
              <FiFile size={12} style={{ color: "var(--accent-purple)" }} />
              <span className="mono">{t(interest.key)}</span>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Main content */}
      <div className="about-page__main">
        {/* Tab bar */}
        <div className="about-page__tabs">
          {tabs.map(({ key, label, icon }) => (
            <button
              key={key}
              className={`about-page__tab${activeTab === key ? " about-page__tab--active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {icon}
              <span className="mono">{t(label)}</span>
              {activeTab === key && (
                <motion.div className="about-page__tab-indicator" layoutId="tab-indicator" />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="about-page__content">
          <AnimatePresence mode="wait">
            {activeTab === "skills" && (
              <motion.div
                key="skills"
                className="about-skills"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="about-bio">
                  <span className="mono" style={{ color: "var(--accent-purple)" }}>
                    {"/**"}
                  </span>
                  <br />
                  <div style={{ whiteSpace: "pre-wrap" }}>{t("AboutMeLong")}</div>
                  <br />
                  <span className="mono" style={{ color: "var(--accent-purple)" }}>
                    {"**/"}
                  </span>
                </div>
                <div className="skills-grid">
                  {skills.map((skill, i) => (
                    <SkillBar key={skill.name} skill={skill} index={i} />
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === "experience" && (
              <motion.div
                key="experience"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="timeline">
                  {experience.map((exp, i) => (
                    <TimelineItem key={exp.company} exp={exp} index={i} t={t} />
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === "certs" && (
              <motion.div
                key="certs"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <CertCarousel />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default AboutMe;
